import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { ActiveCampaignPanel } from '@/spaces/chat/panels/ActiveCampaignPanel';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { CompetitorMonitorPanel } from '@/spaces/chat/panels/CompetitorMonitorPanel';
import { DailySmsJobPanel } from '@/spaces/chat/panels/DailySmsJobPanel';
import { MonitorProcessPanel } from '@/spaces/chat/panels/MonitorProcessPanel';
import { SimilarBusinessInsightsPanel } from '@/spaces/chat/panels/SimilarBusinessInsightsPanel';

import type { ProcessChatPanel } from '../../../../../bridge/types/state.ts';
import { classifyProcessPanel } from '../../../../../bridge/utils/processClassification.ts';

interface ProcessPanelProps {
	panel: ProcessChatPanel;
	onClose: () => void;
	hasHistory?: boolean;
	style?: React.CSSProperties;
}

export const ProcessPanel = ({
	panel,
	onClose,
	hasHistory = false,
	style,
}: ProcessPanelProps) => {
	const bridgeApi = useBridgeApi();
	const onBack = useCallback(() => {
		bridgeApi.sendAction({ type: 'do_pop_chat_panel' });
	}, [bridgeApi]);
	const backHandler = hasHistory ? onBack : undefined;

	if (panel.status === 'loading') {
		return (
			<ChatPanelLayout
				title="Campaign"
				isLoading
				onBack={backHandler}
				onClose={onClose}
				style={style}
			>
				<div />
			</ChatPanelLayout>
		);
	}

	if (panel.status === 'error' || !panel.process) {
		return (
			<ChatPanelLayout
				title="Campaign"
				onBack={backHandler}
				onClose={onClose}
				style={style}
			>
				<div className="flex flex-1 items-center justify-center p-4">
					<p className="text-(--text-primary)">
						{panel.status === 'error'
							? `Failed to load process${panel.error ? `: ${panel.error}` : '.'}`
							: 'Process unavailable'}
					</p>
				</div>
			</ChatPanelLayout>
		);
	}

	switch (classifyProcessPanel(panel.process)) {
		case 'competitor_monitor':
			return (
				<CompetitorMonitorPanel
					panelProcess={panel.process}
					onBack={backHandler}
					onClose={onClose}
					style={style}
				/>
			);
		case 'competitor_video_insights':
			return (
				<SimilarBusinessInsightsPanel
					panelProcess={panel.process}
					onBack={backHandler}
					onClose={onClose}
					style={style}
				/>
			);
		case 'daily_sms':
			return (
				<DailySmsJobPanel
					panelProcess={panel.process}
					onBack={backHandler}
					onClose={onClose}
					style={style}
				/>
			);
		case 'monitor':
			return (
				<MonitorProcessPanel
					panelProcess={panel.process}
					onBack={backHandler}
					onClose={onClose}
					style={style}
				/>
			);
		case 'campaign':
		default:
			return (
				<ActiveCampaignPanel
					panelProcess={panel.process}
					onBack={backHandler}
					onClose={onClose}
					style={style}
				/>
			);
	}
};
